/**
 * Rich Message Components
 *
 * Product cards, order cards, comparison tables, and other rich message types
 * that appear in the chat conversation.
 */

import { formatPrice, formatStatus } from '../../utils/formatting.js';
import { formatRelativeTime } from '../../utils/formatting.js';

/**
 * Create a horizontal product carousel with large image cards
 * @param {Array} products - Array of product objects
 * @param {Object} callbacks - Event callbacks { onProductClick, onAddToCart, onOpenVariantModal, onSendMessage, isInCart }
 * @returns {HTMLElement}
 */
export function createProductCarousel(products, callbacks) {
    const wrapper = document.createElement('div');
    wrapper.className = 'fyncall-product-carousel-wrapper';

    const container = document.createElement('div');
    container.className = 'fyncall-product-carousel';

    const displayProducts = products.slice(0, 6);

    displayProducts.forEach(product => {
        const card = document.createElement('div');
        card.className = 'fyncall-product-card-v2';

        // Card click opens product details
        card.onclick = () => {
            if (product.public_url) {
                window.open(product.public_url, '_blank');
            } else {
                callbacks.onProductClick?.(product);
            }
        };

        // Product image
        const imageDiv = document.createElement('div');
        imageDiv.className = 'fyncall-product-card-image';
        if (product.image_url) {
            const img = document.createElement('img');
            img.src = product.image_url;
            img.alt = product.name || 'Product';
            img.onerror = () => {
                imageDiv.innerHTML = '<div class="fyncall-product-card-placeholder">📦</div>';
            };
            imageDiv.appendChild(img);
        } else {
            imageDiv.innerHTML = '<div class="fyncall-product-card-placeholder">📦</div>';
        }
        card.appendChild(imageDiv);

        // Footer with name and price
        const footer = document.createElement('div');
        footer.className = 'fyncall-product-card-footer';

        const name = document.createElement('span');
        name.className = 'fyncall-product-card-name';
        name.textContent = product.name || 'Product';
        name.title = product.name || 'Product';
        footer.appendChild(name);

        const price = document.createElement('span');
        price.className = 'fyncall-product-card-price';
        price.textContent = '$' + parseFloat(product.price || 0).toFixed(2);
        footer.appendChild(price);

        card.appendChild(footer);

        // Add to Cart button
        const cartBtn = document.createElement('button');
        cartBtn.className = 'fyncall-product-card-btn';
        const isOutOfStock = product.in_stock === false || (product.quantity !== undefined && product.quantity === 0);
        const alreadyInCart = callbacks.isInCart?.(product);

        if (isOutOfStock) {
            cartBtn.textContent = 'Out of Stock';
            cartBtn.disabled = true;
            cartBtn.classList.add('disabled');
        } else if (alreadyInCart) {
            // Product is already in cart - show added state
            cartBtn.textContent = '✓ Added';
            cartBtn.disabled = true;
            cartBtn.classList.add('added');
        } else {
            cartBtn.textContent = 'Add To Cart';
            cartBtn.onclick = (e) => {
                e.stopPropagation();
                const hasVariants = product.variants && product.variants.length > 1;
                if (hasVariants) {
                    callbacks.onOpenVariantModal?.(product);
                } else {
                    // Show loading state
                    cartBtn.textContent = 'Adding...';
                    cartBtn.disabled = true;

                    // Call the callback - handleAddToCart will update button state
                    callbacks.onAddToCart?.(product, cartBtn);
                }
            };
        }
        card.appendChild(cartBtn);

        container.appendChild(card);
    });

    wrapper.appendChild(container);

    // Add navigation arrows if more than 2 products
    if (displayProducts.length > 2) {
        // Left arrow
        const leftArrow = document.createElement('button');
        leftArrow.className = 'fyncall-carousel-arrow fyncall-carousel-arrow-left';
        leftArrow.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';
        leftArrow.onclick = (e) => {
            e.stopPropagation();
            container.scrollBy({ left: -150, behavior: 'smooth' });
        };
        wrapper.appendChild(leftArrow);

        // Right arrow
        const rightArrow = document.createElement('button');
        rightArrow.className = 'fyncall-carousel-arrow fyncall-carousel-arrow-right';
        rightArrow.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
        rightArrow.onclick = (e) => {
            e.stopPropagation();
            container.scrollBy({ left: 150, behavior: 'smooth' });
        };
        wrapper.appendChild(rightArrow);
    }

    // View all link
    if (products.length > 6) {
        const viewAll = document.createElement('div');
        viewAll.className = 'fyncall-product-carousel-view-all';
        viewAll.textContent = `View all ${products.length} products`;
        viewAll.onclick = () => callbacks.onSendMessage?.('Show me more products');
        wrapper.appendChild(viewAll);
    }

    return wrapper;
}

/**
 * Create a single product card
 * @param {Object} product - Product data
 * @param {Object} callbacks - Event callbacks { onProductClick, onAddToCart, onOpenVariantModal }
 * @returns {HTMLElement}
 */
export function createProductCard(product, callbacks = {}) {
    const card = document.createElement('div');
    card.className = 'fyncall-product-card';

    // Product image (clickable to view details) with badges overlay
    const imageContainer = document.createElement('div');
    imageContainer.className = 'fyncall-product-image';
    imageContainer.style.cursor = 'pointer';
    imageContainer.style.position = 'relative';
    imageContainer.onclick = () => callbacks.onProductClick?.(product);

    if (product.image_url) {
        const img = document.createElement('img');
        img.src = product.image_url;
        img.alt = product.name || 'Product';
        img.onerror = () => { imageContainer.innerHTML = '<div class="fyncall-product-image-placeholder">📦</div>'; };
        imageContainer.appendChild(img);
    } else {
        imageContainer.innerHTML = '<div class="fyncall-product-image-placeholder">📦</div>';
    }

    // Sale badge overlay
    const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
    if (hasDiscount) {
        const discount = Math.round((1 - product.price / product.compare_at_price) * 100);
        const saleBadge = document.createElement('div');
        saleBadge.style.cssText = 'position: absolute; top: 4px; left: 4px; background: #dc2626; color: white; font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 4px;';
        saleBadge.textContent = `-${discount}%`;
        imageContainer.appendChild(saleBadge);
    }

    // Low stock badge overlay
    const qty = product.quantity || product.quantity_available || 0;
    if (qty > 0 && qty <= 5) {
        const stockBadge = document.createElement('div');
        stockBadge.style.cssText = 'position: absolute; top: 4px; right: 4px; background: #f59e0b; color: white; font-size: 9px; font-weight: 600; padding: 2px 5px; border-radius: 4px;';
        stockBadge.textContent = `Only ${qty}!`;
        imageContainer.appendChild(stockBadge);
    }

    // Product info
    const info = document.createElement('div');
    info.className = 'fyncall-product-info';

    const name = document.createElement('div');
    name.className = 'fyncall-product-name';
    name.textContent = product.name || 'Unknown Product';
    info.appendChild(name);

    if (product.brand_name) {
        const brand = document.createElement('div');
        brand.className = 'fyncall-product-brand';
        brand.textContent = product.brand_name;
        info.appendChild(brand);
    }

    // Enhanced price display with compare_at_price
    if (product.price) {
        const priceContainer = document.createElement('div');
        priceContainer.className = 'fyncall-product-price';
        priceContainer.style.cssText = 'display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap;';

        const currentPrice = document.createElement('span');
        currentPrice.style.cssText = hasDiscount ? 'color: #dc2626; font-weight: 700;' : '';
        currentPrice.textContent = '$' + parseFloat(product.price).toFixed(2);
        priceContainer.appendChild(currentPrice);

        if (hasDiscount) {
            const originalPrice = document.createElement('span');
            originalPrice.style.cssText = 'text-decoration: line-through; color: #94a3b8; font-size: 11px;';
            originalPrice.textContent = '$' + parseFloat(product.compare_at_price).toFixed(2);
            priceContainer.appendChild(originalPrice);
        }

        info.appendChild(priceContainer);
    }

    // Enhanced stock indicator
    const stockDiv = document.createElement('div');
    stockDiv.style.cssText = 'font-size: 10px; display: flex; align-items: center; gap: 4px;';
    if (qty === 0 || product.in_stock === false) {
        stockDiv.style.color = '#dc2626';
        stockDiv.innerHTML = '❌ Out of Stock';
    } else if (qty <= 5) {
        stockDiv.style.color = '#f59e0b';
        stockDiv.innerHTML = `⚠️ Only ${qty} left!`;
    } else {
        stockDiv.style.color = '#22c55e';
        stockDiv.innerHTML = '✅ In Stock';
    }
    info.appendChild(stockDiv);

    // Variants badge if product has variants
    const hasVariants = product.variants && product.variants.length > 1;
    if (hasVariants) {
        const variantsBadge = document.createElement('div');
        variantsBadge.className = 'fyncall-product-variants-badge';
        variantsBadge.textContent = `${product.variants.length} options`;
        info.appendChild(variantsBadge);
    }

    // Action buttons container
    const actions = document.createElement('div');
    actions.className = 'fyncall-product-actions';

    // View Details button
    const viewBtn = document.createElement('button');
    viewBtn.className = 'fyncall-product-btn secondary';
    viewBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> View';
    viewBtn.onclick = (e) => { e.stopPropagation(); callbacks.onProductClick?.(product); };

    // Add to Cart button - disabled if out of stock
    const cartBtn = document.createElement('button');
    cartBtn.className = 'fyncall-product-btn primary';
    const isOutOfStock = qty === 0 || product.in_stock === false;
    if (isOutOfStock) {
        cartBtn.disabled = true;
        cartBtn.innerHTML = 'Sold Out';
        cartBtn.style.opacity = '0.5';
    } else {
        cartBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1zM20 20c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add';
        cartBtn.onclick = (e) => {
            e.stopPropagation();
            if (hasVariants) {
                callbacks.onOpenVariantModal?.(product);
            } else {
                callbacks.onAddToCart?.(product, cartBtn);
            }
        };
    }

    actions.appendChild(viewBtn);
    actions.appendChild(cartBtn);

    card.appendChild(imageContainer);
    card.appendChild(info);
    card.appendChild(actions);

    return card;
}

/**
 * Create an order status card
 * @param {Object} order - Order data
 * @returns {HTMLElement}
 */
export function createOrderStatusCard(order) {
    const card = document.createElement('div');
    card.className = 'fyncall-order-card';

    // Order header with ID and status
    const header = document.createElement('div');
    header.className = 'fyncall-order-header';

    const orderId = document.createElement('span');
    orderId.className = 'fyncall-order-id';
    orderId.textContent = `Order #${order.order_number || order.id}`;

    const statusBadge = document.createElement('span');
    const status = (order.status || 'processing').toLowerCase();
    statusBadge.className = `fyncall-order-status ${status}`;
    statusBadge.textContent = status.charAt(0).toUpperCase() + status.slice(1);

    header.appendChild(orderId);
    header.appendChild(statusBadge);
    card.appendChild(header);

    return card;
}

/**
 * Create a detailed order card with progress tracker
 * @param {Object} order - Order data
 * @param {Object} callbacks - Event callbacks { onSendMessage, onReorder, onOpenInvoice }
 * @returns {HTMLElement}
 */
export function createOrderDetailCard(order, callbacks = {}) {
    const card = document.createElement('div');
    card.className = 'fyncall-order-detail-card';
    const orderNum = order.order_number || order.order_id;
    const status = (order.status || 'pending').toLowerCase();
    const currency = order.totals?.currency || 'USD';

    // Determine what actions are available based on order status
    const canCancel = ['pending', 'confirmed', 'processing'].includes(status);
    const canChangeAddress = ['pending', 'confirmed', 'processing'].includes(status);
    const canReturn = ['shipped', 'delivered', 'out_for_delivery'].includes(status);
    const hasTracking = order.tracking && (order.tracking.url || order.tracking.number);

    // Header with order info
    const header = document.createElement('div');
    header.className = 'fyncall-order-detail-header';
    header.innerHTML = `
        <div class="fyncall-order-detail-header-top">
            <span class="fyncall-order-detail-number">📦 Order #${orderNum}</span>
            <span class="fyncall-order-detail-status ${status}">${formatStatus(status)}</span>
        </div>
        ${order.placed_at ? `<div class="fyncall-order-detail-date">Placed ${formatRelativeTime(order.placed_at)}</div>` : ''}
    `;
    card.appendChild(header);

    // Status progress tracker
    const progressSteps = ['confirmed', 'processing', 'shipped', 'delivered'];
    const currentStep = progressSteps.indexOf(status);
    if (currentStep >= 0 || status === 'out_for_delivery') {
        const progress = document.createElement('div');
        progress.className = 'fyncall-order-progress';
        let progressHtml = '<div class="fyncall-order-progress-track">';
        progressSteps.forEach((step, idx) => {
            const isActive = idx <= currentStep || (status === 'out_for_delivery' && idx <= 2);
            const isCurrent = step === status || (status === 'out_for_delivery' && step === 'shipped');
            progressHtml += `<div class="fyncall-order-progress-step ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}">
                <div class="fyncall-order-progress-dot"></div>
                <div class="fyncall-order-progress-label">${formatStatus(step)}</div>
            </div>`;
            if (idx < progressSteps.length - 1) {
                progressHtml += `<div class="fyncall-order-progress-line ${isActive && idx < currentStep ? 'active' : ''}"></div>`;
            }
        });
        progressHtml += '</div>';
        progress.innerHTML = progressHtml;
        card.appendChild(progress);
    }

    // Tracking info (if shipped)
    if (hasTracking) {
        const tracking = document.createElement('div');
        tracking.className = 'fyncall-order-detail-tracking';
        let trackingHtml = '<div class="fyncall-order-detail-tracking-info">';
        if (order.tracking.carrier) {
            trackingHtml += `<span class="fyncall-tracking-carrier">🚚 ${order.tracking.carrier}</span>`;
        }
        if (order.tracking.estimated_delivery) {
            trackingHtml += `<span class="fyncall-tracking-eta">📅 Est: ${order.tracking.estimated_delivery}</span>`;
        }
        trackingHtml += '</div>';
        tracking.innerHTML = trackingHtml;
        card.appendChild(tracking);
    }

    // Order items (compact view with +N badge)
    if (order.items && order.items.length > 0) {
        const itemsSection = document.createElement('div');
        itemsSection.className = 'fyncall-order-detail-items';

        // Show first 2 items, badge for rest
        const displayItems = order.items.slice(0, 2);
        const remainingCount = order.items.length - 2;

        displayItems.forEach((item, idx) => {
            const itemDiv = document.createElement('div');
            itemDiv.className = 'fyncall-order-item';
            itemDiv.innerHTML = `
                <div class="fyncall-order-item-image">
                    ${item.image_url ? `<img src="${item.image_url}" alt="${item.name}" onerror="this.parentElement.innerHTML='📦'">` : '📦'}
                    ${idx === 0 && remainingCount > 0 ? `<span class="fyncall-order-item-badge">+${remainingCount}</span>` : ''}
                </div>
                <div class="fyncall-order-item-info">
                    <div class="fyncall-order-item-name">${item.name}</div>
                    ${item.variant_title ? `<div class="fyncall-order-item-variant">${item.variant_title}</div>` : ''}
                    <div class="fyncall-order-item-qty-price">
                        <span class="fyncall-order-item-qty">Qty: ${item.quantity}</span>
                        <span class="fyncall-order-item-price">${formatPrice(item.price, currency)}</span>
                    </div>
                </div>
            `;
            itemsSection.appendChild(itemDiv);
        });
        card.appendChild(itemsSection);
    }

    // Totals (compact)
    if (order.totals) {
        const totals = document.createElement('div');
        totals.className = 'fyncall-order-detail-totals compact';
        totals.innerHTML = `
            <div class="fyncall-order-total-row final">
                <span>Total</span>
                <span>${formatPrice(order.totals.total, currency)}</span>
            </div>
        `;
        card.appendChild(totals);
    }

    // Primary action buttons
    const primaryActions = document.createElement('div');
    primaryActions.className = 'fyncall-order-primary-actions';

    // Track button (if has tracking)
    if (hasTracking && order.tracking.url) {
        const trackBtn = document.createElement('button');
        trackBtn.className = 'fyncall-order-action-btn primary';
        trackBtn.innerHTML = '📍 Track';
        trackBtn.onclick = () => window.open(order.tracking.url, '_blank');
        primaryActions.appendChild(trackBtn);
    } else if (hasTracking) {
        const trackBtn = document.createElement('button');
        trackBtn.className = 'fyncall-order-action-btn primary';
        trackBtn.innerHTML = '📍 Track';
        trackBtn.onclick = () => callbacks.onSendMessage?.(`Track my order ${orderNum}`);
        primaryActions.appendChild(trackBtn);
    }

    // Reorder button
    const reorderBtn = document.createElement('button');
    reorderBtn.className = 'fyncall-order-action-btn primary';
    reorderBtn.innerHTML = '🔄 Reorder';
    reorderBtn.onclick = () => callbacks.onReorder?.(order);
    primaryActions.appendChild(reorderBtn);

    // More options button with dropdown
    const moreContainer = document.createElement('div');
    moreContainer.className = 'fyncall-order-more-container';

    const moreBtn = document.createElement('button');
    moreBtn.className = 'fyncall-order-action-btn more';
    moreBtn.innerHTML = '⋮';
    moreBtn.onclick = (e) => {
        e.stopPropagation();
        const dropdown = moreContainer.querySelector('.fyncall-order-dropdown');
        dropdown.classList.toggle('show');
    };
    moreContainer.appendChild(moreBtn);

    // Dropdown menu
    const dropdown = document.createElement('div');
    dropdown.className = 'fyncall-order-dropdown';

    const menuItems = [
        { icon: '📍', label: 'Track order', action: () => callbacks.onSendMessage?.(`Track my order ${orderNum}`), show: true },
        { icon: '🚚', label: 'Change shipping address', action: () => callbacks.onSendMessage?.(`I need to change the shipping address for order ${orderNum}`), show: canChangeAddress },
        { icon: '❌', label: 'Cancel order', action: () => callbacks.onSendMessage?.(`I want to cancel order ${orderNum}`), show: canCancel },
        { icon: '🔄', label: 'Return/exchange items', action: () => callbacks.onSendMessage?.(`I want to return or exchange items from order ${orderNum}`), show: canReturn },
        { icon: '📄', label: 'View invoice', action: () => callbacks.onOpenInvoice?.(order), show: true },
        { icon: '💬', label: 'Get help', action: () => callbacks.onSendMessage?.(`I need help with order ${orderNum}`), show: true },
    ];

    menuItems.forEach(item => {
        if (item.show) {
            const menuItem = document.createElement('div');
            menuItem.className = 'fyncall-order-dropdown-item';
            menuItem.innerHTML = `<span class="fyncall-dropdown-icon">${item.icon}</span><span>${item.label}</span><span class="fyncall-dropdown-arrow">›</span>`;
            menuItem.onclick = (e) => {
                e.stopPropagation();
                dropdown.classList.remove('show');
                item.action();
            };
            dropdown.appendChild(menuItem);
        }
    });

    moreContainer.appendChild(dropdown);
    primaryActions.appendChild(moreContainer);
    card.appendChild(primaryActions);

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
        dropdown.classList.remove('show');
    });

    return card;
}

/**
 * Create quick action cards grid
 * @param {Object} data - { title, actions: [{ icon, label, description, action }] }
 * @param {Function} onSendMessage - Callback to send message
 * @returns {HTMLElement}
 */
export function createQuickActionCards(data, onSendMessage) {
    const container = document.createElement('div');
    container.className = 'fyncall-quick-actions-card';

    if (data.title) {
        container.innerHTML = `<div class="fyncall-quick-actions-title">${data.title}</div>`;
    }

    const grid = document.createElement('div');
    grid.className = 'fyncall-quick-actions-grid';

    const defaultActions = [
        { icon: '🛍️', label: 'Shop Now', description: 'Browse our collection', action: 'Show me products' },
        { icon: '📦', label: 'Track Order', description: 'Check your order status', action: 'Track my order' },
        { icon: '🎫', label: 'Get Help', description: 'FAQs and support', action: 'I need help' },
        { icon: '💬', label: 'Contact Us', description: 'Talk to a human', action: 'Talk to a human agent' }
    ];

    const actions = data.actions || defaultActions;

    actions.forEach(action => {
        const item = document.createElement('div');
        item.className = 'fyncall-quick-action-item';
        item.onclick = () => onSendMessage?.(action.action || action.label);
        item.innerHTML = `
            <div class="fyncall-quick-action-icon">${action.icon}</div>
            <div class="fyncall-quick-action-label">${action.label}</div>
            <div class="fyncall-quick-action-desc">${action.description}</div>
        `;
        grid.appendChild(item);
    });

    container.appendChild(grid);
    return container;
}

/**
 * Create a collection browser for products
 * @param {Object} data - { title, products, total_count }
 * @param {Function} onSendMessage - Callback to send message
 * @returns {HTMLElement}
 */
export function createCollectionBrowser(data, onSendMessage) {
    const container = document.createElement('div');
    container.className = 'fyncall-collection-browser';

    // Header
    const header = document.createElement('div');
    header.className = 'fyncall-collection-header';
    header.innerHTML = `🔥 ${data.title || 'Featured Collection'}`;
    container.appendChild(header);

    // Products scroll
    const products = document.createElement('div');
    products.className = 'fyncall-collection-products';

    (data.products || []).forEach(product => {
        const item = document.createElement('div');
        item.className = 'fyncall-collection-product';
        item.onclick = () => onSendMessage?.(`Tell me about ${product.name}`);

        const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
        const discount = hasDiscount ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

        item.innerHTML = `
            <div class="fyncall-collection-product-image">
                ${product.image_url ? `<img src="${product.image_url}" alt="${product.name}" onerror="this.parentElement.innerHTML='📦'">` : '📦'}
            </div>
            <div class="fyncall-collection-product-info">
                <div class="fyncall-collection-product-price">$${parseFloat(product.price).toFixed(2)}</div>
                ${hasDiscount ? `<div class="fyncall-collection-product-original">$${parseFloat(product.compare_at_price).toFixed(2)}</div>` : ''}
                ${hasDiscount ? `<div class="fyncall-collection-product-discount">-${discount}%</div>` : ''}
            </div>
        `;
        products.appendChild(item);
    });
    container.appendChild(products);

    // Footer
    const footer = document.createElement('div');
    footer.className = 'fyncall-collection-footer';
    footer.innerHTML = `
        <span class="fyncall-collection-count">Showing ${(data.products || []).length} of ${data.total_count || (data.products || []).length} items</span>
        <span class="fyncall-collection-view-all">View All →</span>
    `;
    footer.querySelector('.fyncall-collection-view-all').onclick = () =>
        onSendMessage?.(`Show all ${data.title || 'sale'} items`);
    container.appendChild(footer);

    return container;
}

/**
 * Create a ticket status card
 * @param {Object} ticket - Ticket data
 * @returns {HTMLElement}
 */
export function createTicketStatusCard(ticket) {
    const card = document.createElement('div');
    card.className = 'fyncall-ticket-card';

    const status = (ticket.status || 'open').toLowerCase();
    const priority = (ticket.priority || 'normal').toLowerCase();

    card.innerHTML = `
        <div class="fyncall-ticket-header">
            <span class="fyncall-ticket-id">🎫 #${ticket.ticket_number || ticket.id}</span>
            <span class="fyncall-ticket-status ${status}">${formatStatus(status)}</span>
        </div>
        <div class="fyncall-ticket-subject">${ticket.subject || 'Support Request'}</div>
        ${ticket.created_at ? `<div class="fyncall-ticket-date">Created ${formatRelativeTime(ticket.created_at)}</div>` : ''}
        <div class="fyncall-ticket-priority ${priority}">Priority: ${priority.charAt(0).toUpperCase() + priority.slice(1)}</div>
    `;

    return card;
}

/**
 * Create a product comparison table
 * @param {Array} products - Products to compare
 * @param {Object} callbacks - Event callbacks
 * @returns {HTMLElement}
 */
export function createComparisonTable(products, callbacks = {}) {
    const container = document.createElement('div');
    container.className = 'fyncall-comparison-table';

    // Header row with product images
    const headerRow = document.createElement('div');
    headerRow.className = 'fyncall-comparison-header';
    headerRow.innerHTML = '<div class="fyncall-comparison-cell label"></div>';

    products.forEach(product => {
        headerRow.innerHTML += `
            <div class="fyncall-comparison-cell product">
                <div class="fyncall-comparison-image">
                    ${product.image_url ? `<img src="${product.image_url}" alt="${product.name}">` : '📦'}
                </div>
                <div class="fyncall-comparison-name">${product.name}</div>
            </div>
        `;
    });
    container.appendChild(headerRow);

    // Price row
    const priceRow = document.createElement('div');
    priceRow.className = 'fyncall-comparison-row';
    priceRow.innerHTML = '<div class="fyncall-comparison-cell label">Price</div>';
    products.forEach(product => {
        priceRow.innerHTML += `<div class="fyncall-comparison-cell">$${parseFloat(product.price).toFixed(2)}</div>`;
    });
    container.appendChild(priceRow);

    // Stock row
    const stockRow = document.createElement('div');
    stockRow.className = 'fyncall-comparison-row';
    stockRow.innerHTML = '<div class="fyncall-comparison-cell label">Availability</div>';
    products.forEach(product => {
        const inStock = product.in_stock !== false && (product.quantity || 0) > 0;
        stockRow.innerHTML += `<div class="fyncall-comparison-cell ${inStock ? 'in-stock' : 'out-stock'}">${inStock ? '✅ In Stock' : '❌ Out of Stock'}</div>`;
    });
    container.appendChild(stockRow);

    // Action row
    const actionRow = document.createElement('div');
    actionRow.className = 'fyncall-comparison-row actions';
    actionRow.innerHTML = '<div class="fyncall-comparison-cell label"></div>';
    products.forEach(product => {
        const cell = document.createElement('div');
        cell.className = 'fyncall-comparison-cell';
        const btn = document.createElement('button');
        btn.className = 'fyncall-comparison-btn';
        btn.textContent = 'Add to Cart';
        btn.onclick = () => callbacks.onAddToCart?.(product, btn);
        cell.appendChild(btn);
        actionRow.appendChild(cell);
    });
    container.appendChild(actionRow);

    return container;
}

/**
 * Get category icon based on name
 * @param {string} categoryName - Category name
 * @returns {string} Emoji icon
 */
export function getCategoryIcon(categoryName) {
    const icons = {
        'shoes': '👟', 'footwear': '👟', 'sneakers': '👟',
        'bags': '👜', 'handbags': '👜', 'purses': '👜',
        'clothing': '👕', 'apparel': '👕', 'clothes': '👕',
        'accessories': '⌚', 'jewelry': '💎', 'watches': '⌚',
        'electronics': '📱', 'phones': '📱', 'laptops': '💻',
        'home': '🏠', 'furniture': '🛋️', 'decor': '🖼️',
        'sports': '⚽', 'fitness': '🏋️', 'outdoor': '🏕️',
        'beauty': '💄', 'skincare': '🧴', 'cosmetics': '💅',
        'kids': '🧸', 'toys': '🎮', 'baby': '👶',
        'food': '🍔', 'grocery': '🛒', 'drinks': '🍹'
    };
    const name = (categoryName || '').toLowerCase();
    for (const [key, icon] of Object.entries(icons)) {
        if (name.includes(key)) return icon;
    }
    return '📦';
}

/**
 * Create an info card (tip, warning, success)
 * @param {Object} info - Info card data
 * @param {Object} callbacks - Event callbacks { onSendMessage }
 * @returns {HTMLElement}
 */
export function createInfoCard(info, callbacks = {}) {
    const card = document.createElement('div');
    const cardType = info.type || 'tip';
    card.className = `fyncall-info-card ${cardType}`;

    // Header
    const header = document.createElement('div');
    header.className = 'fyncall-info-card-header';

    const icon = document.createElement('span');
    icon.className = 'fyncall-info-card-icon';
    icon.textContent = cardType === 'tip' ? '💡' : cardType === 'warning' ? '⚠️' : '✅';
    header.appendChild(icon);

    if (info.title) {
        const title = document.createElement('span');
        title.className = 'fyncall-info-card-title';
        title.textContent = info.title;
        header.appendChild(title);
    }
    card.appendChild(header);

    // Content
    if (info.content) {
        const content = document.createElement('div');
        content.className = 'fyncall-info-card-content';
        content.textContent = info.content;
        card.appendChild(content);
    }

    // Action
    if (info.action) {
        const action = document.createElement('span');
        action.className = 'fyncall-info-card-action';
        action.textContent = info.action.label || 'Learn more →';
        action.onclick = () => {
            if (info.action.url) {
                window.open(info.action.url, '_blank');
            } else if (info.action.message && callbacks.onSendMessage) {
                callbacks.onSendMessage(info.action.message);
            }
        };
        card.appendChild(action);
    }

    return card;
}

/**
 * Create a feature list
 * @param {Array} features - Feature items
 * @returns {HTMLElement}
 */
export function createFeatureList(features) {
    const container = document.createElement('div');
    container.className = 'fyncall-feature-list';

    features.forEach(feature => {
        const item = document.createElement('div');
        item.className = 'fyncall-feature-item';

        const icon = document.createElement('span');
        icon.className = `fyncall-feature-icon ${feature.included ? 'check' : 'cross'}`;
        icon.textContent = feature.included ? '✓' : '✗';

        const text = document.createElement('span');
        text.textContent = feature.text || feature;

        item.appendChild(icon);
        item.appendChild(text);
        container.appendChild(item);
    });

    return container;
}

/**
 * Create a minimalist category browser
 * @param {Object} data - Category browser data
 * @param {Object} callbacks - Event callbacks { onSendMessage }
 * @returns {HTMLElement}
 */
export function createCategoryBrowser(data, callbacks = {}) {
    const container = document.createElement('div');
    container.className = 'fyncall-category-browser';

    const categories = data.categories || [];
    const displayCategories = categories.slice(0, 5);

    displayCategories.forEach((category) => {
        const item = document.createElement('div');
        item.className = 'fyncall-category-item';
        item.onclick = () => {
            if (callbacks.onSendMessage) {
                callbacks.onSendMessage(`Show me ${category.name} products`);
            }
        };

        // Category icon
        const iconDiv = document.createElement('div');
        iconDiv.className = 'fyncall-category-icon';
        if (category.image_url) {
            const img = document.createElement('img');
            img.src = category.image_url;
            img.alt = category.name;
            img.onerror = () => { iconDiv.innerHTML = getCategoryIcon(category.name); };
            iconDiv.appendChild(img);
        } else {
            iconDiv.innerHTML = getCategoryIcon(category.name);
        }
        item.appendChild(iconDiv);

        // Category info
        const info = document.createElement('div');
        info.className = 'fyncall-category-item-info';
        info.innerHTML = `
            <div class="fyncall-category-item-name">${category.name}</div>
            <div class="fyncall-category-item-count">${category.product_count || 0} items</div>
        `;
        item.appendChild(info);

        container.appendChild(item);
    });

    // View all link if more categories
    if (data.show_view_all !== false && categories.length > 5) {
        const viewAll = document.createElement('div');
        viewAll.className = 'fyncall-category-view-all';
        viewAll.innerHTML = `View all ${categories.length} categories`;
        viewAll.onclick = () => {
            if (callbacks.onSendMessage) {
                callbacks.onSendMessage('Show all categories');
            }
        };
        container.appendChild(viewAll);
    }

    return container;
}
